import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { ltr } from './arabic';
import { i18n } from './i18n';
import type { TagRef } from './tracking';
import { useOrgId } from './org';

export type SalesKind =
  'invoice' | 'credit_note' | 'sales_receipt' | 'quote' | 'sales_order' | 'delivery_challan';
/** Never posted, numbered when saved, Open or Closed (migration 1801). */
export const ORDER_KINDS: readonly SalesKind[] = ['sales_order', 'delivery_challan'];
export type ChallanType = 'job_work' | 'supply_on_approval' | 'liquid_gas' | 'others';
/** Rule 55 of the CGST Rules and section 31(7): why goods move under a delivery challan. */
export const CHALLAN_TYPES: Array<[ChallanType, string]> = [
  ['job_work', 'Job work'],
  ['supply_on_approval', 'Supply on approval'],
  ['liquid_gas', 'Supply of liquid gas'],
  ['others', 'Other than by way of supply'],
];
export type AmountsAre = 'exclusive' | 'inclusive' | 'out_of_scope';

export interface DocumentLine {
  lineNo: number;
  itemId: string | null;
  itemName: string | null;
  sku: string | null;
  description: string | null;
  quantity: string;
  unitPrice: string;
  accountId: string;
  accountName: string;
  taxCodeId: string;
  taxCode: string;
  taxRate: string;
  /** India: HSN or SAC code. */
  hsnSac?: string | null;
  net: string;
  tax: string;
  gross: string;
  /** The line in home currency at the document's rate. */
  homeNet: string;
  homeTax: string;
  /** Class and project of the line (migration 1700). */
  classId?: string | null;
  className?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  /** The sales order and challan lines this line fulfils (migration 1801). */
  soLineId?: string | null;
  dcLineId?: string | null;
  /** Sales orders and challans: line id, closed by hand, quantities invoiced and delivered. */
  id?: string;
  closed?: boolean;
  invoicedQuantity?: string;
  deliveredQuantity?: string;
}

/** A document linked by its lines (the orders an invoice fulfils, the invoices of an order). */
export interface LinkedDocument {
  id: string;
  kind: SalesKind;
  number: string | null;
  status: string;
  issueDate: string;
}

export interface Allocation {
  id: string;
  date: string;
  amount: string;
  type: 'payment' | 'credit_note' | 'invoice' | 'journal_entry';
  refId: string;
  refNumber: string;
  /** Realised exchange gain (positive) or loss in home currency, if any. */
  exchangeGainLoss: string | null;
}

export interface SalesDocument {
  /** Class, location, project and tags (migration 1700). */
  classId?: string | null;
  className?: string | null;
  locationId?: string | null;
  locationName?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  tags?: TagRef[];
  id: string;
  kind: SalesKind;
  number: string | null;
  status: string;
  overdue: boolean;
  contactId: string;
  contactName: string;
  contactTrn: string | null;
  contactVatRegistered: boolean;
  customerEmail: string | null;
  billingAddress: Record<string, string | null> | null;
  issueDate: string;
  dueDate: string | null;
  termsDays: number | null;
  supplyDate: string | null;
  placeOfSupply: string | null;
  amountsAre: AmountsAre;
  currency: string;
  fxRate: string;
  homeCurrency: string;
  subtotal: string;
  tax: string;
  total: string;
  balance: string;
  homeSubtotal: string;
  homeTax: string;
  homeTotal: string;
  taxSummary: Array<{ taxCode: string; rate: string; net: string; tax: string; homeTax: string }>;
  reference: string | null;
  memo: string | null;
  statementMemo: string | null;
  creditReason: string | null;
  depositAccountId: string | null;
  depositAccountName: string | null;
  paymentMethod: string | null;
  relatedDocumentId: string | null;
  relatedDocumentNumber: string | null;
  relatedDocumentKind: string | null;
  journalEntryId: string | null;
  voidEntryId: string | null;
  voidReason: string | null;
  sentAt: string | null;
  version: number;
  createdAt: string;
  approvedAt: string | null;
  approvedByName: string | null;
  /** Open item brought over from QuickBooks at the conversion date: no lines, read-only. */
  opening: boolean;
  /** Delivery challans: why the goods move. */
  challanType?: ChallanType | null;
  sources?: LinkedDocument[];
  fulfilledBy?: LinkedDocument[];
  lines: DocumentLine[];
  allocations: Allocation[];
  warnings: string[];
}

export interface SalesRow {
  id: string;
  type: SalesKind | 'payment';
  number: string | null;
  date: string;
  dueDate: string | null;
  contactId: string;
  contactName: string;
  memo: string | null;
  total: string;
  balance: string;
  currency: string;
  homeTotal: string;
  homeBalance: string;
  status: string;
  overdue: boolean;
  sentAt: string | null;
}

type Bucket = { amount: string; count: number };
export interface SalesSummary {
  overdue: Bucket;
  notDueYet: Bucket;
  openInvoices: Bucket;
  notDeposited: Bucket;
  depositedLast30: Bucket;
  paidLast30: Bucket;
  quotes: Bucket;
  drafts: Bucket;
  unappliedCredits: Bucket;
}

export interface Payment {
  id: string;
  number: string;
  status: 'posted' | 'void';
  contactId: string;
  contactName: string;
  date: string;
  depositAccountId: string;
  depositAccountName: string;
  method: string | null;
  reference: string | null;
  memo: string | null;
  amount: string;
  unapplied: string;
  currency: string;
  fxRate: string;
  homeCurrency: string;
  homeAmount: string;
  journalEntryId: string;
  voidEntryId: string | null;
  voidReason: string | null;
  allocations: Allocation[];
}

export interface OpenItems {
  /** The customer's currency (every amount below is in it). */
  currency: string;
  homeCurrency: string;
  /** Open invoices and journal entry charges on A/R (kind journal_entry). */
  invoices: Array<{
    kind: 'invoice' | 'journal_entry';
    id: string;
    number: string;
    /** The journal entry of a journal_entry row. */
    entryId: string | null;
    issueDate: string;
    dueDate: string | null;
    total: string;
    balance: string;
    overdue: boolean;
    fxRate: string;
  }>;
  credits: Array<{
    kind: 'credit_note' | 'payment' | 'journal_entry';
    id: string;
    number: string;
    entryId: string | null;
    date: string;
    total: string;
    balance: string;
  }>;
}

/**
 * Routes and English names of each kind. The names stay English: they name downloaded PDFs. On
 * screen use kindLabel, kindNumberLabel and kindDateLabel.
 */
export const KIND_INFO: Record<
  SalesKind,
  { label: string; path: string; listPath: string; numberLabel: string; dateLabel: string }
> = {
  invoice: {
    label: 'Invoice',
    path: '/sales/invoices',
    listPath: '/sales/invoices',
    numberLabel: 'Invoice no.',
    dateLabel: 'Invoice date',
  },
  credit_note: {
    label: 'Credit note',
    path: '/sales/credit-notes',
    listPath: '/sales/all',
    numberLabel: 'Credit note no.',
    dateLabel: 'Credit note date',
  },
  sales_receipt: {
    label: 'Sales receipt',
    path: '/sales/sales-receipts',
    listPath: '/sales/all',
    numberLabel: 'Sales receipt no.',
    dateLabel: 'Sales receipt date',
  },
  quote: {
    label: 'Quote',
    path: '/sales/quotes',
    listPath: '/sales/quotes',
    numberLabel: 'Quote no.',
    dateLabel: 'Quote date',
  },
  sales_order: {
    label: 'Sales order',
    path: '/sales/sales-orders',
    listPath: '/sales/sales-orders',
    numberLabel: 'SO no.',
    dateLabel: 'Sales order date',
  },
  delivery_challan: {
    label: 'Delivery challan',
    path: '/sales/delivery-challans',
    listPath: '/sales/delivery-challans',
    numberLabel: 'Challan no.',
    dateLabel: 'Challan date',
  },
};

/** Where a sales row opens. */
export function rowPath(r: Pick<SalesRow, 'type' | 'id'>): string {
  return r.type === 'payment' ? `/sales/payments/${r.id}` : `${KIND_INFO[r.type].path}/${r.id}`;
}

/** QBO status wording (document 05 StatusBadge), in the reader's language. */
export function statusLabel(r: { type?: string; kind?: string; status: string; overdue: boolean }) {
  const kind = r.kind ?? r.type;
  const s = (k: keyof typeof STATUS_KEYS) => i18n.t(`salesPages.status.${k}`);
  if (r.status === 'void') return kind === 'delivery_challan' ? s('cancelled') : s('void');
  if (r.status === 'draft') return s('draft');
  if (kind === 'purchase_order' || kind === 'sales_order' || kind === 'delivery_challan')
    return r.status === 'open' ? s('open') : s('closed');
  if (kind === 'quote')
    return r.status === 'pending' ||
      r.status === 'accepted' ||
      r.status === 'declined' ||
      r.status === 'converted'
      ? s(r.status)
      : r.status;
  if (kind === 'payment' || kind === 'vendor_payment')
    return r.status === 'posted' ? s('closed') : r.status;
  if (kind === 'credit_note' || kind === 'debit_note')
    return r.status === 'paid'
      ? s('applied')
      : r.status === 'partially_paid'
        ? s('partlyApplied')
        : s('unapplied');
  if (kind === 'sales_receipt' || kind === 'expense') return s('paid');
  if (r.status === 'paid') return s('paid');
  if (r.overdue) return r.status === 'partially_paid' ? s('overduePartlyPaid') : s('overdue');
  if (r.status === 'partially_paid') return s('partiallyPaid');
  return s('unpaid');
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- only its keys are used, as a type
const STATUS_KEYS = {
  cancelled: 1,
  void: 1,
  draft: 1,
  open: 1,
  closed: 1,
  pending: 1,
  accepted: 1,
  declined: 1,
  converted: 1,
  applied: 1,
  partlyApplied: 1,
  unapplied: 1,
  paid: 1,
  overduePartlyPaid: 1,
  overdue: 1,
  partiallyPaid: 1,
  unpaid: 1,
} as const;

/** A sales document type's name ("Invoice", "Payment") in the reader's language. */
export const kindLabel = (kind: SalesRow['type']): string => i18n.t(`salesPages.kind.${kind}`);
/** "Invoice no." and the like, in the reader's language. */
export const kindNumberLabel = (kind: SalesKind): string => i18n.t(`salesPages.kindNumber.${kind}`);
/** "Invoice date" and the like, in the reader's language. */
export const kindDateLabel = (kind: SalesKind): string => i18n.t(`salesPages.kindDate.${kind}`);
/** A payment method's name (PAYMENT_METHODS value) in the reader's language. */
export function paymentMethodLabel(value: string | null | undefined): string {
  const m = PAYMENT_METHODS.find((x) => x.value === value);
  return m ? i18n.t(`salesPages.paymentMethod.${m.value as 'cash'}`) : '';
}
/** Why goods move under a delivery challan, in the reader's language. */
export const challanTypeLabel = (t: ChallanType): string => i18n.t(`salesPages.challanType.${t}`);
/** A sent email's status in the reader's language. */
export const emailStatusLabel = (s: SentEmail['status']): string =>
  i18n.t(`salesPages.emailStatus.${s}`);

export function useSalesDocument(id: string | undefined) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['sales', orgId, 'document', id],
    queryFn: () => api<SalesDocument>(`/orgs/${orgId}/sales/documents/${id}`),
    enabled: Boolean(orgId && id),
  });
}

export function useSalesDocuments(kind?: SalesKind, contactId?: string) {
  const orgId = useOrgId();
  const qs = new URLSearchParams();
  if (kind) qs.set('kind', kind);
  if (contactId) qs.set('contactId', contactId);
  return useQuery({
    queryKey: ['sales', orgId, 'documents', kind ?? 'all', contactId ?? 'all'],
    queryFn: () => api<SalesDocument[]>(`/orgs/${orgId}/sales/documents?${qs.toString()}`),
    enabled: Boolean(orgId),
  });
}

export function useSalesTransactions(contactId?: string) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['sales', orgId, 'transactions', contactId ?? 'all'],
    queryFn: () =>
      api<SalesRow[]>(
        `/orgs/${orgId}/sales/transactions${contactId ? `?contactId=${contactId}` : ''}`,
      ),
    enabled: Boolean(orgId),
  });
}

export function useSalesSummary(contactId?: string) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['sales', orgId, 'summary', contactId ?? 'all'],
    queryFn: () =>
      api<SalesSummary>(
        `/orgs/${orgId}/sales/summary${contactId ? `?contactId=${contactId}` : ''}`,
      ),
    enabled: Boolean(orgId),
  });
}

export function useOpenItems(contactId: string | null) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['sales', orgId, 'open-items', contactId],
    queryFn: () => api<OpenItems>(`/orgs/${orgId}/sales/open-items?contactId=${contactId}`),
    enabled: Boolean(orgId && contactId),
  });
}

export function usePayment(id: string | undefined) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['sales', orgId, 'payment', id],
    queryFn: () => api<Payment>(`/orgs/${orgId}/sales/payments/${id}`),
    enabled: Boolean(orgId && id),
  });
}

// ---------- line arithmetic, mirroring ledger-core computeSalesLine (bigint, no floats) ----------

function divRound(n: bigint, d: bigint): bigint {
  const q = n / d;
  const r = n % d;
  const twice = (r < 0n ? -r : r) * 2n;
  if (twice >= d) return n < 0n ? q - 1n : q + 1n;
  return q;
}

/** "2.5" → { digits: 25n, scale: 1 }; null if not a positive decimal. */
export function parseQuantity(text: string): { digits: bigint; scale: number } | null {
  const m = /^(\d*)(?:\.(\d*))?$/.exec(text.replace(/,/g, '').trim());
  if (!m || (m[1] === '' && (m[2] ?? '') === '')) return null;
  const frac = m[2] ?? '';
  if (frac.length > 6) return null;
  const digits = BigInt((m[1] || '0') + frac);
  return digits > 0n ? { digits, scale: frac.length } : null;
}

export function lineAmounts(
  quantity: string,
  unitPriceMinor: bigint,
  rateBp: number,
  amountsAre: AmountsAre,
): { net: bigint; tax: bigint; gross: bigint } {
  const q = parseQuantity(quantity);
  if (!q) return { net: 0n, tax: 0n, gross: 0n };
  const amount = divRound(unitPriceMinor * q.digits, 10n ** BigInt(q.scale));
  const bp = amountsAre === 'out_of_scope' ? 0n : BigInt(rateBp);
  if (amountsAre === 'inclusive') {
    const tax = divRound(amount * bp, 10000n + bp);
    return { net: amount - tax, tax, gross: amount };
  }
  const tax = divRound(amount * bp, 10000n);
  return { net: amount, tax, gross: amount + tax };
}

/** Rate (minor units) that gives `amountMinor` for `quantity`, rounded to the fils. */
export function rateFor(amountMinor: bigint, quantity: string): bigint | null {
  const q = parseQuantity(quantity);
  if (!q) return null;
  return divRound(amountMinor * 10n ** BigInt(q.scale), q.digits);
}

export const PAYMENT_METHODS: Array<{ value: string; label: string }> = [
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Credit card' },
];

/** The Send dialog's prefill (company sales email defaults filled in by the API). */
export interface EmailDraft {
  to: string;
  cc: string;
  subject: string;
  body: string;
  attachmentName: string;
  /** False when the server records messages on Sent emails instead of delivering them. */
  delivers: boolean;
  /** Language of the message and the attached document (document 15). */
  language?: 'en' | 'ar' | 'both';
  /** Arabic is on but this server prints in English only. */
  arabicUnavailable?: boolean;
}

export interface SentEmail {
  id: string;
  documentId: string | null;
  documentNumber: string | null;
  documentKind: SalesKind | null;
  from: string;
  to: string;
  cc: string | null;
  subject: string;
  body: string;
  attachmentName: string | null;
  transport: string;
  status: 'recorded' | 'sent' | 'failed';
  error: string | null;
  sentBy: string | null;
  createdAt: string;
}

export interface BatchSendResult {
  sent: SentEmail[];
  skipped: Array<{ id: string; number: string | null; reason: string }>;
}

export interface ActivityEntry {
  id: string;
  at: string;
  action: string;
  label: string;
  detail: string | null;
  by: string | null;
}

/** A document's history from the audit log, newest first. */
export function useDocumentActivity(id: string | undefined) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['sales', orgId, 'activity', id],
    queryFn: () => api<ActivityEntry[]>(`/orgs/${orgId}/sales/documents/${id}/activity`),
    enabled: Boolean(orgId && id),
  });
}

export type BatchAction = 'send' | 'print' | 'void';

/** Whether a list row can take a batch action (payments have their own screens). */
export function canBatch(action: BatchAction, r: Pick<SalesRow, 'type' | 'status'>): boolean {
  if (r.type === 'payment') return false;
  if (action === 'print') return true;
  if (action === 'send') return !['draft', 'void', 'converted'].includes(r.status);
  return r.type !== 'quote' && r.status !== 'draft' && r.status !== 'void';
}

export interface BatchVoidResult {
  voided: number;
  /** Refused by the server: the period lock, a filed VAT return, an accepted e-invoice. */
  refused: Array<{ number: string | null; reason: string }>;
  /** Left out before calling the server: quotes, drafts, payments, already void. */
  skipped: Array<{ number: string | null; reason: string }>;
}

function skipReason(r: Pick<SalesRow, 'type' | 'status'>): string {
  if (r.type === 'payment') return i18n.t('salesPages.list.skipPayment');
  if (r.type === 'quote') return i18n.t('salesPages.list.skipQuote');
  if (r.status === 'draft') return i18n.t('salesPages.list.skipDraft');
  return i18n.t('salesPages.list.skipVoid');
}

/**
 * Voids each row the batch can void, one at a time through `voidOne` (the document's own void
 * endpoint, so the database's period, VAT return and e-invoice locks decide each one), and
 * collects what was voided, what the server refused and why, and what was skipped.
 */
export async function batchVoid(
  rows: SalesRow[],
  voidOne: (r: SalesRow) => Promise<unknown>,
): Promise<BatchVoidResult> {
  const out: BatchVoidResult = { voided: 0, refused: [], skipped: [] };
  for (const r of rows) {
    if (!canBatch('void', r)) {
      out.skipped.push({ number: r.number, reason: skipReason(r) });
      continue;
    }
    try {
      await voidOne(r);
      out.voided += 1;
    } catch (e) {
      out.refused.push({ number: r.number, reason: e instanceof Error ? e.message : String(e) });
    }
  }
  return out;
}

/**
 * A number, code or amount inside a translated sentence built outside React: isolated left to
 * right for Arabic readers, untouched in English.
 */
export const ltrIn = (s: string): string => (i18n.language === 'ar' ? ltr(s) : s);

/** "INV-2 (reason); INV-3 (reason)" in the reader's language. */
export const reasonList = (xs: Array<{ number: string | null; reason: string }>): string =>
  xs
    .map((x) =>
      i18n.t('salesPages.list.listItem', {
        number: x.number ? ltrIn(x.number) : i18n.t('salesPages.list.draftLower'),
        reason: x.reason.replace(/\.$/, ''),
      }),
    )
    .join(i18n.t('salesPages.list.listSeparator'));

/** The toast after a batch void. */
export function batchVoidMessage(r: BatchVoidResult): string {
  return [
    i18n.t('salesPages.list.voidedCount', { count: r.voided }),
    r.refused.length ? i18n.t('salesPages.list.notVoided', { list: reasonList(r.refused) }) : null,
    r.skipped.length ? i18n.t('salesPages.list.skipped', { list: reasonList(r.skipped) }) : null,
  ]
    .filter(Boolean)
    .join(' ');
}
